import React, { useMemo } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Typography,
  Stack,
  useTheme,
  useMediaQuery,
  CardContent,
} from '@mui/material';
import { CheckSquare } from 'lucide-react';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectActiveTimers, startTimerAsync } from '@/store/slices/timerSlice';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { Task } from '@/types';
import {
  TaskTableConfig,
  TaskContextType,
  TaskTableCallbacks,
} from './types';
import { COLUMN_DEFINITIONS } from './columns';
import { MOBILE_BREAKPOINT } from './constants';
import { createTaskLookupMap, getParentTask } from './utils';
import { getTaskPermissions } from './permissions';
import TaskTableRow from './TaskTableRow';
import MobileTaskCard from './MobileTaskCard';
import { useTodaysPickedTaskIds } from '@/hooks/useTodaysPickedTaskIds';
import { useSnackbar } from '@/shared/contexts/SnackbarContext';
import { PICK_BEFORE_TIMER_MESSAGE } from '@/shared/constants/timerRules';

export interface TaskTableProps extends Partial<TaskTableCallbacks> {
  tasks: Task[];
  isLoading: boolean;
  context: TaskContextType;
  config: TaskTableConfig;
  currentRaciRole?: 'responsible' | 'accountable' | 'consulted' | 'informed';
  emptyStateMessage?: string;
  emptyStateAction?: React.ReactNode;
}

/**
 * TaskTable - Main shared task table component
 * Handles responsive rendering, loading states, and task interactions
 */
const TaskTable: React.FC<TaskTableProps> = ({
  tasks,
  isLoading,
  context,
  config,
  currentRaciRole,
  emptyStateMessage = 'No tasks found',
  emptyStateAction,
  onTaskClick = () => {},
  onCheckboxChange = () => {},
  onStartTimer,
  onMenuAction = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BREAKPOINT));
  const dispatch = useAppDispatch();
  const activeTimers = useAppSelector(selectActiveTimers);
  const user = useAppSelector(selectCurrentUser);
  const todaysPickedTaskIds = useTodaysPickedTaskIds();
  const { showError } = useSnackbar();

  // Create task lookup map for O(1) parent task access
  const taskMap = useMemo(() => createTaskLookupMap(tasks), [tasks]);

  // Handle timer start
  const handleStartTimer = (taskId: string, taskTitle: string) => {
    if (!todaysPickedTaskIds.has(taskId)) {
      showError(PICK_BEFORE_TIMER_MESSAGE);
      return;
    }
    if (onStartTimer) {
      onStartTimer(taskId, taskTitle);
    } else if (user) {
      // Default timer start logic
      dispatch(startTimerAsync({ taskId, userId: user.id, taskTitle }));
    }
  };

  /**
   * Render loading skeleton
   */
  const renderLoadingSkeleton = () => {
    if (isMobile && config.mobileEnabled) {
      return (
        <Stack spacing={1.5}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i} sx={{ borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Skeleton variant="rectangular" width={20} height={20} sx={{ borderRadius: 0.5 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="70%" height={20} />
                    <Skeleton variant="text" width="40%" height={16} />
                  </Box>
                  <Skeleton variant="circular" width={28} height={28} />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      );
    }

    // Desktop loading skeleton
    return (
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {config.columns.map((col) => {
                  const def = COLUMN_DEFINITIONS[col];
                  return (
                    <TableCell
                      key={col}
                      width={def.width}
                      align={def.align}
                      padding={col === 'checkbox' ? 'checkbox' : 'normal'}
                    >
                      {def.label}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {[1, 2, 3, 4, 5].map((i) => (
                <TableRow key={i}>
                  {config.columns.map((col) => (
                    <TableCell key={col}>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    );
  };

  /**
   * Render empty state
   */
  const renderEmptyState = () => {
    if (isMobile && config.mobileEnabled) {
      return (
        <Card sx={{ borderRadius: 2 }}>
          <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
            <CheckSquare size={48} style={{ color: theme.palette.grey[400], marginBottom: 16 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {emptyStateMessage}
            </Typography>
            {emptyStateAction}
          </Box>
        </Card>
      );
    }

    return (
      <Card sx={{ borderRadius: { xs: 2, md: 1 } }}>
        <Box sx={{ py: { xs: 6, md: 8 }, px: 2, textAlign: 'center' }}>
          <CheckSquare size={48} style={{ color: theme.palette.grey[400], marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {emptyStateMessage}
          </Typography>
          {emptyStateAction}
        </Box>
      </Card>
    );
  };

  // Show loading skeleton
  if (isLoading) {
    return renderLoadingSkeleton();
  }

  // Show empty state
  if (tasks.length === 0) {
    return renderEmptyState();
  }

  /**
   * Render mobile cards
   */
  if (isMobile && config.mobileEnabled) {
    return (
      <Stack spacing={1.5}>
        {tasks.map((task) => {
          const parentTask = getParentTask(task, taskMap);
          const isTimerActive = activeTimers.some((t) => t.taskId === task.id);
          const isPickedForToday = todaysPickedTaskIds.has(task.id);
          const permissions = getTaskPermissions(task, user, context, currentRaciRole);

          return (
            <MobileTaskCard
              key={task.id}
              task={task}
              context={context}
              currentRaciRole={currentRaciRole}
              isTimerActive={isTimerActive}
              isPickedForToday={isPickedForToday}
              showProjectName={config.showProjectName || false}
              parentTask={parentTask}
              user={user}
              permissions={permissions}
              onTaskClick={onTaskClick}
              onCheckboxChange={onCheckboxChange}
              onStartTimer={handleStartTimer}
            />
          );
        })}
      </Stack>
    );
  }

  /**
   * Render desktop table
   */
  return (
    <Card variant="outlined">
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {config.columns.map((col) => {
                const def = COLUMN_DEFINITIONS[col];
                return (
                  <TableCell
                    key={col}
                    width={def.width}
                    align={def.align}
                    padding={col === 'checkbox' ? 'checkbox' : 'normal'}
                  >
                    <Typography variant="subtitle2" fontWeight={600}>
                      {def.label}
                    </Typography>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => {
              const parentTask = getParentTask(task, taskMap);
              const isTimerActive = activeTimers.some((t) => t.taskId === task.id);
              const isPickedForToday = todaysPickedTaskIds.has(task.id);
              const permissions = getTaskPermissions(task, user, context, currentRaciRole);

              return (
                <TaskTableRow
                  key={task.id}
                  task={task}
                  columns={config.columns}
                  context={context}
                  currentRaciRole={currentRaciRole}
                  isTimerActive={isTimerActive}
                  isPickedForToday={isPickedForToday}
                  showProjectName={config.showProjectName || false}
                  parentTask={parentTask}
                  user={user}
                  permissions={permissions}
                  onTaskClick={onTaskClick}
                  onCheckboxChange={onCheckboxChange}
                  onStartTimer={handleStartTimer}
                  onMenuAction={onMenuAction}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default TaskTable;
